import type { CSSProperties, ReactElement } from "react";
import type { Site } from "@/types/sites";
import { useEffect } from "react";
import Script from "next/script";
import { DEFAULT_DATALAYER_NAME } from "../../../constants";

export const Analytics = ({ site }: { site: Site | null }): ReactElement => {
    const siteAttrs = site?.attributes;
    const googleTagManagerContainerId = siteAttrs?.googleTagManagerContainerId;
    const googleOptimizeContainerId = siteAttrs?.googleOptimizeContainerId;
    const iframeStyles: CSSProperties = {
        display: "none",
        visibility: "hidden",
    };

    // TODO: the dataLayer name can be configured (https://web.archive.org/web/20230330030444/https://developers.google.com/tag-platform/tag-manager/web/datalayer)
    // It looks like most people (including QiH) only ever use the default. If in future they want
    // to configure it then we can put it in the CMS along side the container id and then access it
    // here. For now, set it to be a copy of the default.
    const cmsGoogleTagManagerDataLayer = encodeURIComponent(
        JSON.parse(JSON.stringify(DEFAULT_DATALAYER_NAME))
    );
    const urlDataLayer =
        cmsGoogleTagManagerDataLayer != DEFAULT_DATALAYER_NAME
            ? `&l=${cmsGoogleTagManagerDataLayer}`
            : "";

    // prevent `prepWindowForGtm` from being run on the server, `window` is only avalible client side.
    // in dev mode components are rendered twice which means our data layer ends up with 2 `"gtm.start"` events.
    // this does not happen in production (or any other environment serving with `npm run start` instead of `npm run develop`)
    // https://stackoverflow.com/a/60619061/498463
    useEffect(() => {
        googleTagManagerContainerId &&
            prepWindowForGtm(cmsGoogleTagManagerDataLayer);
    });

    // Google tag manager "install" instructions specifically says:
    //  - Paste this code [jg: the script tag] as high in the <head> of the page as possible
    //
    // If we use the builtin nextjs <Script> tag then we have no control over its position and they
    // end up at the bottom of the body tag.
    // Google-ing around I don't believe this is a problem, however I'm noting this here in case we
    // experience issues with it in future. If we do, I think we will need to implement NextJS' `_document.tsx`
    // https://nextjs.org/docs/advanced-features/custom-document
    // alternatively, we can simply alter the `strategy` to be `strategy="beforeInteractive"` but that
    // causes IDE errors/warnings so also needs an eslint-disable-next-line comment - so using `_document.tsx`
    // is probably best as that is the "designed" way to use it.
    return (<>
        {/* Google support docs say that googleoptimize should appear above googletagmanager BUT
        the datalayer initialization should happen before googleoptimize */}
        {googleOptimizeContainerId && (
            // 2023-03-30: JG & MP spoke to Connor@qih he mentioned that this can sometimes cause a page flicker
            // If this happens to us too then we should first look at the comment above regarding
            // `_document.tsx` and/or `beforeInteractive` and only after experimenting with those then
            // fall back to looking at this: https://support.google.com/optimize/answer/7100284
            (<Script
                id="googleoptimize"
                src={`https://www.googleoptimize.com/optimize.js?id=${encodeURIComponent(
                    googleOptimizeContainerId
                )}`}
                strategy="afterInteractive"
            />)
        )}
        {googleTagManagerContainerId && (
            <>
                <Script
                    id="googletagmanager"
                    src={`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
                        googleTagManagerContainerId
                    )}${urlDataLayer}`}
                    strategy="afterInteractive"
                />

                <noscript>
                    <iframe
                        id="googletagmanager-noscript"
                        src={`https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
                            googleTagManagerContainerId
                        )}${urlDataLayer}`}
                        height="0"
                        width="0"
                        style={iframeStyles}
                    />
                </noscript>
            </>
        )}
    </>);
};

const prepWindowForGtm = (dataLayerName: string): void => {
    /**
     * Typescript compiler says that Window objects can't be indexed by a string.
     *
     *  - Element implicitly has an 'any' type because index expression is not of type 'number'.ts(7015)
     *
     * ¯\_(ツ)_/¯
     *
     * StackOverflow suggests typecasting or overloading the interface - https://stackoverflow.com/q/42193262/498463
     * However, both suggestions seem like bad ideas to me:
     *  - we would have to typecast in 3 different places just on this 5 line function
     *  - if we overload the interface we lose IDE hints and spell check e.g. window.getElmentById
     *    would not be flagged as an error in the IDE (typo in Element).
     *
     * @ts-expect-error */
    window[dataLayerName] = window[dataLayerName] || [];

    /**
     * same here - attempting to index window by string.
     * Also - typescript can't ignore at function level, only single line or whole file...
     * https://github.com/Microsoft/TypeScript/issues/19573
     * @ts-expect-error */
    window[dataLayerName].push({
        "gtm.start": Date.now(),
        event: "gtm.js",
    });
};
